// Espelho em TypeScript do protocolo definido em server/internal/world/protocol.go.
// Se mudar um lado, mude o outro.

/**
 * Avatar montado no customizador. Mesmo formato do servidor (model.Appearance)
 * e do catalogo gerado em src/avatar/catalog.json.
 */
export interface Appearance {
  skinTone: number;
  hair: string;
  hairColor: number;
  top: string;
  topColor: number;
  bottom: string;
  bottomColor: number;
  shoes: string;
  shoesColor: number;
  /** Segunda cor das pecas de cima estampadas (xadrez, listrada...). */
  topColor2: number;
  /** Acessorios: "nenhum" quando nao ha peca (ver CATALOG.none). */
  facialHair: string;
  headwear: string;
  headwearColor: number;
  glasses: string;
  earrings: string;
}

export interface PublicUser {
  id: string;
  username: string;
  /** Apelido publico (chat, mundo, ranking) — cai para `username` se nunca foi escolhido. */
  displayName: string;
  skin: number;
  /** null = ainda nao montou o avatar: vale o preset `skin` e o customizador abre no login. */
  appearance: Appearance | null;
  /** Clicou no link de confirmacao. Opcional: nao bloqueia nada, so some o lembrete. */
  emailVerified: boolean;
  /** Cargo da conta: admins entram no painel /admin. */
  role: Role;
}

export type Role = "player" | "admin";

export interface MapInfo {
  key: string;
  url: string;
  width: number;
  height: number;
  tileSize: number;
  pixelWidth: number;
  pixelHeight: number;
  spawnX: number;
  spawnY: number;
}

export interface Interactable {
  id: number;
  name: string;
  /** "minigame" abre um jogo; "leaderboard" abre o painel de recordes; "seat" senta; "info" e uma placa. */
  kind: string;
  game: string;
  label: string;
  /** Texto das placas ("info"). */
  text?: string;
  x: number;
  y: number;
  radius: number;
  /** So nos assentos: onde o jogador fica de pe ao levantar. */
  standX?: number;
  standY?: number;
}

export const KIND_SEAT = "seat";

export interface PlayerInfo {
  id: string;
  name: string;
  skin: number;
  /** ausente = desenha o preset `skin` */
  appearance?: Appearance;
  x: number;
  y: number;
}

/** Direcoes, na mesma ordem das linhas do spritesheet do avatar. */
export const DIRECTIONS = ["down", "left", "right", "up"] as const;
export type Direction = (typeof DIRECTIONS)[number];

export interface PlayerState {
  id: string;
  x: number;
  y: number;
  /** indice da direcao */
  d: number;
  /** esta se movendo */
  m: boolean;
  /** id do assento ocupado; ausente = de pe */
  s?: number;
  /** gesto em loop (id de CATALOG.gestures); ausente = nenhum */
  g?: string;
  /** aura ativa (easter egg); ausente = nenhuma */
  a?: string;
  /** conversa trancada de que participa (chat de voz); ausente = nenhuma */
  v?: string;
  /** veio de outra instancia do servidor */
  r?: boolean;
}

export interface WelcomeMessage {
  t: "welcome";
  you: PlayerInfo | null;
  tickRate: number;
  snapshotRate: number;
  walkSpeed: number;
  map: MapInfo;
  interactables: Interactable[];
  online: number;
  serverTime: number;
  foxy?: FoxyInfo;
  /** ausente = servidor sem chat de voz */
  voice?: VoiceInfo;
}

export interface StateMessage {
  t: "state";
  tick: number;
  joined?: PlayerInfo[];
  left?: string[];
  players: PlayerState[];
  foxy?: FoxyState;
  ack?: number;
  /** progresso (0 a 1) da carga de aura do proprio jogador; ausente = sem barrinha */
  charge?: number;
  online: number;
}

export interface ChatMessage {
  t: "chat";
  id: string;
  name: string;
  text: string;
  at: number;
}

export interface InteractMessage {
  t: "interact";
  kind: string;
  game?: string;
  label?: string;
  name?: string;
  text?: string;
}

export interface SystemMessage {
  t: "system";
  message: string;
  level?: "info" | "warn" | "error";
  /** Avisos que exigem tratamento especial, ex.: SESSION_REPLACED. */
  code?: string;
}

/** A conta abriu em outra aba: esta conexao caiu e nao deve reconectar. */
export const SESSION_REPLACED = "session_replaced";

/**
 * Codigos do admin que derrubam a conexao (server/internal/world/protocol.go).
 * O cliente nao reconecta sozinho: volta para a tela de entrada ou, no
 * profile_reset, recarrega o perfil e entra de novo.
 */
export const KICK_CODES = ["deactivated", "session_ended", "profile_reset"] as const;
export type KickCode = (typeof KICK_CODES)[number];
export const PROFILE_RESET = "profile_reset";
/** Avisos que tambem aparecem em destaque no topo da tela. */
export const BANNER_CODES = ["announce"];

export interface PongMessage {
  t: "pong";
  ts: number;
}

/** A Foxy NPC no snapshot (ver server/internal/world/foxy.go). */
export interface FoxyState {
  x: number;
  y: number;
  d: number;
  /** "walk", "idle" ou "sit" */
  p: string;
}

/** Vem no welcome quando o mapa tem a Foxy. */
export interface FoxyInfo {
  label: string;
  radius: number;
}

/** Alguem fez carinho na Foxy; notice = mostrar a linha no chat. */
export interface FoxyMessage {
  t: "foxy";
  ev: "pet";
  id: string;
  name: string;
  notice?: boolean;
}

/** Chat de voz (ver server/internal/world/voice.go). */
export interface VoiceInfo {
  /** ate onde se ouve na area aberta (px, pe a pe) */
  hearRadius: number;
  /** ate onde o volume e cheio */
  fullRadius: number;
  zones: VoiceZone[];
}

/** Sala de voz do mapa: quem esta dentro so ouve quem tambem esta. */
export interface VoiceZone {
  name: string;
  label: string;
  x: number;
  y: number;
  w: number;
  h: number;
  ellipse?: boolean;
}

export type VoiceKind = "open" | "zone" | "locked";

/** Em que sala do LiveKit entrar (chega na entrada e a cada troca). */
export interface VoiceMessage {
  t: "voice";
  room: string;
  token: string;
  /** vazio = o mesmo endereco do jogo (/rtc) */
  url?: string;
  kind: VoiceKind;
  label: string;
}

/** Alguem quer entrar na conversa trancada. */
export interface VoiceKnockMessage {
  t: "voice_knock";
  id: string;
  name: string;
}

/** Mensagem privada nova (texto ou aviso do grupo), ver server/internal/world/dm.go. */
export interface DMPushMessage {
  t: "dm";
  message: DMMessage;
}

export type ServerMessage =
  | WelcomeMessage
  | DMPushMessage
  | VoiceMessage
  | VoiceKnockMessage
  | FoxyMessage
  | StateMessage
  | ChatMessage
  | InteractMessage
  | SystemMessage
  | PongMessage;

// --------------------------------------------------------------- REST

export interface Game {
  slug: string;
  name: string;
  maxScore: number;
}

export interface LeaderboardEntry {
  rank: number;
  userId: string;
  username: string;
  skin: number;
  score: number;
  createdAt: string;
}

export interface LeaderboardResponse {
  game: Game;
  entries: LeaderboardEntry[];
  personalBest?: number;
}

export interface ScoreResult {
  score: number;
  personalBest: number;
  isNewBest: boolean;
  rank: number;
}

// --------------------------------------------------------- admin

/** So resta desativar a conta (gravado como "ban", o nome historico). */
export type SanctionKind = "ban";

export interface Sanction {
  id: number;
  userId: string;
  kind: SanctionKind;
  reason: string;
  issuedBy: string;
  createdAt: string;
  /** null = permanente */
  expiresAt: string | null;
  revokedAt: string | null;
  revokedBy?: string;
}

export interface ActiveSanctions {
  /** desativacao em vigor */
  ban: Sanction | null;
}

export interface OnlinePlayer {
  id: string;
  name: string;
  username: string;
  role: Role;
}

export interface AdminUser {
  id: string;
  username: string;
  /** "" = usa o nome de usuario */
  displayName: string;
  email: string;
  emailVerified: boolean;
  role: Role;
  createdAt: string;
  online: boolean;
  sanctions: ActiveSanctions;
}

export interface ModAction {
  id: number;
  actorId: string | null;
  actorName: string;
  targetId: string | null;
  targetName: string;
  action: string;
  detail: string;
  createdAt: string;
}

export interface Page {
  total: number;
  page: number;
  pageSize: number;
}

export interface AdminUserDetail {
  user: AdminUser;
  canModerate: boolean;
  history: Sanction[];
  actions: ModAction[];
  scores: { game: string; best: number; plays: number }[];
}

/** Um item do checklist do painel admin (ver health_handlers.go). */
export interface HealthCheck {
  group: string;
  label: string;
  status: "ok" | "warn" | "error" | "info";
  detail: string;
  hint?: string;
}

/** Token para a sala de diagnostico do teste de voz do painel admin. */
export interface VoiceTestToken {
  /** vazio = o mesmo endereco do jogo (/rtc) */
  url: string;
  token: string;
  tcpPort: number;
  udpPort: number;
}

// ------------------------------------------------- mensagens privadas

/** Limites das mensagens privadas (iguais aos de server/internal/model/dm.go). */
export const DM_MAX_CHARS = 500;
export const DM_GROUP_NAME_MAX = 100;
export const DM_GROUP_MAX = 100;

export interface DMUser {
  id: string;
  name: string;
  skin: number;
  /** ausente = o preset `skin` */
  appearance?: Appearance;
}

/** kind "system": aviso do grupo (entrou, saiu, renomeou). Sem senderId = conta excluida. */
export interface DMMessage {
  id: number;
  conversationId: string;
  senderId?: string;
  senderName: string;
  kind: "text" | "system";
  body: string;
  at: string;
}

export interface DMConversation {
  id: string;
  kind: "direct" | "group";
  /** so nos grupos */
  name?: string;
  /** inclui o proprio jogador */
  members: DMUser[];
  last?: DMMessage;
  unread: number;
  lastReadId: number;
  lastMessageAt: string;
}

// --------------------------------------------------------------- dossie

/** Campos do dossie que a pessoa pode esconder (iguais aos de server/internal/model/dossier.go). */
export type DossierField = "team" | "job" | "age" | "birthday" | "location";

export const DOSSIER_TEXT_MAX = 60;
export const TEAM_NAME_MAX = 40;

export const UFS = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA",
  "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO",
] as const;

export interface Team {
  id: number;
  name: string;
  members: number;
}

/** O dossie como o dono edita. birthDate em AAAA-MM-DD ("" = nao informada). */
export interface Dossier {
  teamId: number | null;
  jobTitle: string;
  birthDate: string;
  city: string;
  state: string;
  hidden: DossierField[];
}

/** O dossie que os outros veem: so o preenchido e nao escondido. */
export interface PublicDossier {
  id: string;
  name: string;
  skin: number;
  appearance?: Appearance;
  team?: string;
  jobTitle?: string;
  age?: number;
  /** dd/mm */
  birthday?: string;
  city?: string;
  state?: string;
  memberSince: string;
  online: boolean;
  /** so no proprio dossie: o que o dono escondeu dos outros */
  hidden?: DossierField[];
}
